import { ShoppingBagIcon } from 'lucide-react';
import { useMemo } from 'react';

import { OrderStatus } from '@invenco/sales-interface/interfaces';

import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useRequiredParam } from '@/shared/hooks/use-required-param';
import { orderStatusType } from '@/shared/status-maps';
import { orderStatusTitle } from '@/shared/title-maps';

import { EditableOrderLinesCard, OrderLinesCard } from '../order-lines-card';
import { FulfillmentCard } from './fulfillment-card/fulfillment-card';
import { ManagedAlert } from './managed-alert';
import { OrderActions } from './order-actions';
import { OrderDetailTabs } from './order-detail-tabs';
import { OrderTotalsCard } from './order-totals-card';
import { useOrder } from './use-order';

export function OrderDetailsShad() {
  const id = useRequiredParam('id');
  const {
    order,
    shipments,
    shipmentsError,
    isLoading,
    isLoadingShipments,
    refetchOrder,
    addOrderLine,
    updateOrder,
    updateOrderLine,
    applyManualHold,
    releaseManualHold,
    revertToDraft,
    releaseOrder,
    cancelOrder,
    closeOrder,
  } = useOrder(id);
  const orderContext = { currency: order?.currency, channelId: order?.channelId };
  const lines = order?.orderLines ?? [];
  const isEditable = order?.status === OrderStatus.DRAFT;
  const shipmentsByFulfillmentId = useMemo(() => {
    const map: Record<string, typeof shipments> = {};

    shipments.forEach((shipment) => {
      const id = shipment.shippableId;
      if (!id) return;

      map[id] = map[id] ?? [];
      map[id].push(shipment);
    });

    return map;
  }, [shipments]);

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/sales/orders" parentTitle="Orders">
          <ShoppingBagIcon />
        </HeaderIcon>
        <HeaderTitle pageTitle={order?.name} className="flex flex-wrap items-center gap-2">
          {order?.name}
          {order?.status ? (
            <StatusBadge status={order.status} type={orderStatusType} title={orderStatusTitle} />
          ) : null}
        </HeaderTitle>
        <OrderActions
          order={order}
          isLoading={isLoading}
          onRefresh={refetchOrder}
          updateOrder={updateOrder}
          applyManualHold={applyManualHold}
          releaseManualHold={releaseManualHold}
          revertToDraft={revertToDraft}
          releaseOrder={releaseOrder}
          cancelOrder={cancelOrder}
          closeOrder={closeOrder}
        />
      </Header>
      <ManagedAlert order={order} />
      <PageContent variant="split">
        <DetailMain>
          {order?.fulfillments?.map((fulfillment) => (
            <FulfillmentCard
              key={fulfillment.id}
              fulfillment={fulfillment}
              shipments={shipmentsByFulfillmentId[fulfillment.id] ?? []}
              qtyOrdered={order.qtyOrdered}
              isLoading={isLoading || isLoadingShipments}
              shipmentsError={shipmentsError}
            />
          ))}
          {isEditable ? (
            <EditableOrderLinesCard
              title="Items"
              lines={lines}
              order={orderContext}
              isLoading={isLoading}
              initialVisibleLineCount={5}
              onAddLine={addOrderLine}
              onUpdateLine={updateOrderLine}
            />
          ) : (
            <OrderLinesCard
              title="Items"
              lines={lines}
              order={orderContext}
              isLoading={isLoading}
              initialVisibleLineCount={5}
            />
          )}
          <OrderTotalsCard order={order} lines={lines} isLoading={isLoading} />
        </DetailMain>
        <DetailSide className="@5xl:w-112">
          <OrderDetailTabs order={order} shipments={shipments} isLoading={isLoading} />
        </DetailSide>
      </PageContent>
    </Page>
  );
}
